import { useCallback, useState } from 'react';

import { notifications } from '@mantine/notifications';

import { vnextApiClient } from '@/services/vnext-api';
import type { Subject } from '@/types';
import type { ConversationAttachment } from '@/types/attachment';
import type {
  DirectConfirmCandidate,
  DirectExtractedCandidate,
  DirectRequestDraft,
  ReviewCandidate,
} from '@/types/direct-request';

export type DirectRequestPhase = 'compose' | 'extracting' | 'review' | 'submitting' | 'submitted';

export interface DirectConfirmOutcome {
  mode: 'direct' | 'triage';
  reports: Record<string, Subject[]>;
  reportRequestIds: string[];
}

/**
 * State machine for the direct request flow:
 *
 *   compose → (extract) → extracting → review → (confirm) → submitting → submitted
 *
 * Files are uploaded while composing; the first upload lazily mints the
 * draft ReportRequest + chat thread that anchor them. Extraction returns
 * editable candidates; nothing is created until confirm.
 */
export function useDirectRequest() {
  const [phase, setPhase] = useState<DirectRequestPhase>('compose');
  const [draft, setDraft] = useState<DirectRequestDraft | null>(null);
  const [files, setFiles] = useState<ConversationAttachment[]>([]);
  // Files the user unticked from "use for subject extraction". They stay
  // uploaded and attached to the request (supporting documents) — they
  // just don't feed the extraction context.
  const [extractionExcluded, setExtractionExcluded] = useState<Set<string>>(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const [candidates, setCandidates] = useState<ReviewCandidate[]>([]);
  const [needsHumanReview, setNeedsHumanReview] = useState(false);
  const [result, setResult] = useState<DirectConfirmOutcome | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const uploadFiles = useCallback(
    async (newFiles: File[], teamId?: string | null): Promise<ConversationAttachment[]> => {
      setIsUploading(true);
      setError(null);
      try {
        let currentDraft = draft;
        if (!currentDraft) {
          currentDraft = await vnextApiClient.createDirectRequestDraft(teamId);
          setDraft(currentDraft);
        }
        const uploaded: ConversationAttachment[] = [];
        for (const file of newFiles) {
          const attachments = await vnextApiClient.uploadConversationAttachment(
            currentDraft.chat_thread_id,
            file
          );
          uploaded.push(...attachments);
        }
        setFiles((prev) => [...prev, ...uploaded]);
        return uploaded;
      } catch (err) {
        const uploadError = err instanceof Error ? err : new Error('Failed to upload files');
        setError(uploadError);
        notifications.show({ title: 'Upload failed', message: uploadError.message, color: 'red' });
        throw uploadError;
      } finally {
        setIsUploading(false);
      }
    },
    [draft]
  );

  /** Excludes the file from the request. The row stays on the draft's
   *  thread (attachments always end up analyst-reviewed anyway). */
  const removeFile = useCallback((attachmentId: string) => {
    setFiles((prev) =>
      prev.filter((f) => f.id !== attachmentId && f.parentAttachmentId !== attachmentId)
    );
    setExtractionExcluded((prev) => {
      if (!prev.has(attachmentId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(attachmentId);
      return next;
    });
  }, []);

  /** Tick/untick a file for subject extraction. Unticking a zip also
   *  unticks its extracted members (the zip row itself carries no
   *  content — the members do). */
  const setUseForExtraction = useCallback(
    (attachmentId: string, use: boolean) => {
      setExtractionExcluded((prev) => {
        const next = new Set(prev);
        const affected = [
          attachmentId,
          ...files.filter((f) => f.parentAttachmentId === attachmentId).map((f) => f.id),
        ];
        for (const id of affected) {
          if (use) {
            next.delete(id);
          } else {
            next.add(id);
          }
        }
        return next;
      });
    },
    [files]
  );

  const extract = useCallback(
    async (
      text: string,
      teamId?: string | null,
      reportConfigId?: string | null,
      opts?: {
        /** Ignore any held files/draft — used by the enquiry-confirmation
         * escape hatch, where the draft has already been absorbed into
         * the enquiry and only the text carries over. */
        textOnly?: boolean;
        /** Screening explicitly selected (screening intake modes):
         * candidates stay config-less and derivation is suppressed. */
        screeningPinned?: boolean;
      }
    ): Promise<DirectExtractedCandidate[]> => {
      setPhase('extracting');
      setError(null);
      try {
        const extractionFiles = opts?.textOnly
          ? []
          : files.filter((f) => !extractionExcluded.has(f.id));
        const useDraft = !opts?.textOnly && files.length > 0;
        const response = await vnextApiClient.extractDirectRequest({
          text,
          draft_id: useDraft ? draft?.report_request_id : undefined,
          attachment_ids: extractionFiles.map((f) => f.id),
          team_id: teamId ?? undefined,
          // Explicit selection wins — the backend stamps it on every
          // candidate and skips LLM config derivation.
          report_config_id: reportConfigId ?? undefined,
          screening_pinned: opts?.screeningPinned || undefined,
        });
        setCandidates(response.candidates);
        setNeedsHumanReview(Boolean(response.needs_human_review));
        setPhase('review');
        return response.candidates;
      } catch (err) {
        const extractError = err instanceof Error ? err : new Error('Failed to extract subjects');
        setError(extractError);
        setPhase('compose');
        notifications.show({
          title: 'Extraction failed',
          message: extractError.message,
          color: 'red',
        });
        throw extractError;
      }
    },
    [draft, files, extractionExcluded]
  );

  const confirm = useCallback(
    async (params: {
      candidates: DirectConfirmCandidate[];
      separateReports?: boolean;
      expedited?: boolean;
      teamId?: string | null;
    }): Promise<DirectConfirmOutcome> => {
      setPhase('submitting');
      setError(null);
      try {
        const anyAttachments = params.candidates.some(
          (c) => (c.attachment_ids ?? []).length > 0 || (c.sources ?? []).length > 0
        );
        const outcome = await vnextApiClient.confirmDirectRequest({
          draft_id: anyAttachments ? draft?.report_request_id : undefined,
          team_id: params.teamId ?? undefined,
          candidates: params.candidates,
          separate_reports: params.separateReports,
          expedited: params.expedited,
        });
        setResult(outcome);
        setPhase('submitted');

        if (outcome.mode === 'triage') {
          notifications.show({
            title: 'Submitted for review',
            message:
              'Your request has been submitted to the analyst team. ' +
              "We'll be in touch shortly.",
            color: 'green',
          });
        } else {
          const subjectCount = Object.values(outcome.reports).flat().length;
          const reportCount = Object.keys(outcome.reports).length;
          notifications.show({
            title: 'Report requested',
            message:
              reportCount === 1
                ? `${subjectCount} subject(s) submitted for screening`
                : `${subjectCount} subject(s) submitted across ${reportCount} reports`,
            color: 'green',
          });
        }
        return outcome;
      } catch (err) {
        const confirmError = err instanceof Error ? err : new Error('Failed to submit request');
        setError(confirmError);
        setPhase('review');
        notifications.show({
          title: 'Submission failed',
          message: confirmError.message,
          color: 'red',
        });
        throw confirmError;
      }
    },
    [draft]
  );

  /**
   * Async submit for FILE-BEARING requests: no extraction, no review —
   * the draft goes straight to analyst triage and files digest in the
   * background. Instant feedback for the client (long/fragile digestion
   * used to block the extract step with an unbounded spinner).
   */
  const submitWithFiles = useCallback(
    async (params: {
      text: string;
      reportConfigId?: string | null;
      expedited?: boolean;
    }): Promise<DirectConfirmOutcome> => {
      if (!draft) {
        throw new Error('No draft to submit — upload a file first.');
      }
      setPhase('submitting');
      setError(null);
      try {
        const response = await vnextApiClient.submitDirectRequest({
          draft_id: draft.report_request_id,
          text: params.text,
          report_config_id: params.reportConfigId ?? undefined,
          expedited: params.expedited,
        });
        const outcome: DirectConfirmOutcome = {
          mode: response.mode,
          reports: {},
          reportRequestIds: response.reportRequestIds,
        };
        setResult(outcome);
        setPhase('submitted');
        notifications.show({
          title: 'Request received',
          message:
            "We're processing your files and an analyst will review your " +
            'request. Track progress under Requests.',
          color: 'green',
        });
        return outcome;
      } catch (err) {
        const submitError = err instanceof Error ? err : new Error('Failed to submit request');
        setError(submitError);
        setPhase('compose');
        notifications.show({
          title: 'Submission failed',
          message: submitError.message,
          color: 'red',
        });
        throw submitError;
      }
    },
    [draft]
  );

  /** Back from review to composing, keeping text/files/draft intact. */
  const backToCompose = useCallback(() => {
    setPhase('compose');
    setCandidates([]);
    setNeedsHumanReview(false);
  }, []);

  /** Full reset for a fresh request (a new draft is minted on next upload). */
  const reset = useCallback(() => {
    setPhase('compose');
    setDraft(null);
    setFiles([]);
    setExtractionExcluded(new Set());
    setCandidates([]);
    setNeedsHumanReview(false);
    setResult(null);
    setError(null);
  }, []);

  return {
    phase,
    draft,
    files,
    extractionExcluded,
    isUploading,
    candidates,
    setCandidates,
    needsHumanReview,
    result,
    error,
    uploadFiles,
    removeFile,
    setUseForExtraction,
    extract,
    confirm,
    submitWithFiles,
    backToCompose,
    reset,
  };
}
